import { memo, useCallback, useRef, useState } from 'react';
import { XIcon } from '@heroicons/react/outline';
import { AnnotationMarker } from '../types';

interface MarkerPinProps {
  marker: AnnotationMarker;
  isActive: boolean;
  containerRef: React.RefObject<HTMLDivElement | null>;
  onClick: () => void;
  onMove: (x: number, y: number) => void;
  onDelete: () => void;
}

function MarkerPin({
  marker,
  isActive,
  containerRef,
  onClick,
  onMove,
  onDelete,
}: MarkerPinProps) {
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef<{
    x: number;
    y: number;
    markerX: number;
    markerY: number;
  } | null>(null);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        markerX: marker.position.x,
        markerY: marker.position.y,
      };

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!dragStartRef.current) return;

        const deltaX = moveEvent.clientX - dragStartRef.current.x;
        const deltaY = moveEvent.clientY - dragStartRef.current.y;

        // Only start dragging if moved more than 5px
        if (!isDragging && (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5)) {
          setIsDragging(true);
        }

        if (isDragging || Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
          const newX =
            dragStartRef.current.markerX + (deltaX / rect.width) * 100;
          const newY =
            dragStartRef.current.markerY + (deltaY / rect.height) * 100;

          // Clamp to bounds
          const clampedX = Math.max(0, Math.min(100, newX));
          const clampedY = Math.max(0, Math.min(100, newY));

          onMove(clampedX, clampedY);
        }
      };

      const handleMouseUp = () => {
        if (!isDragging) {
          onClick();
        }
        setIsDragging(false);
        dragStartRef.current = null;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [containerRef, marker.position, isDragging, onClick, onMove],
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      e.stopPropagation();

      const container = containerRef.current;
      if (!container) return;

      const touch = e.touches[0];
      const rect = container.getBoundingClientRect();
      dragStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        markerX: marker.position.x,
        markerY: marker.position.y,
      };

      const handleTouchMove = (moveEvent: TouchEvent) => {
        if (!dragStartRef.current) return;
        moveEvent.preventDefault();

        const moveTouch = moveEvent.touches[0];
        const deltaX = moveTouch.clientX - dragStartRef.current.x;
        const deltaY = moveTouch.clientY - dragStartRef.current.y;

        if (!isDragging && (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5)) {
          setIsDragging(true);
        }

        if (isDragging || Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
          const newX =
            dragStartRef.current.markerX + (deltaX / rect.width) * 100;
          const newY =
            dragStartRef.current.markerY + (deltaY / rect.height) * 100;

          const clampedX = Math.max(0, Math.min(100, newX));
          const clampedY = Math.max(0, Math.min(100, newY));

          onMove(clampedX, clampedY);
        }
      };

      const handleTouchEnd = () => {
        if (!isDragging) {
          onClick();
        }
        setIsDragging(false);
        dragStartRef.current = null;
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };

      document.addEventListener('touchmove', handleTouchMove, {
        passive: false,
      });
      document.addEventListener('touchend', handleTouchEnd);
    },
    [containerRef, marker.position, isDragging, onClick, onMove],
  );

  const handleDeleteClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onDelete();
    },
    [onDelete],
  );

  return (
    <div
      className='absolute group'
      style={{
        left: `${marker.position.x}%`,
        top: `${marker.position.y}%`,
        transform: 'translate(-50%, -50%)',
        zIndex: isActive ? 20 : 10,
      }}
    >
      {/* Main marker */}
      <div
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        className={`
          relative w-7 h-7 sm:w-8 sm:h-8 rounded-full
          flex items-center justify-center
          text-white text-sm font-semibold
          cursor-pointer select-none
          transition-all duration-200
          ${
            isActive
              ? 'bg-orange-600 shadow-lg shadow-orange-500/40 scale-110 ring-2 ring-white ring-offset-2'
              : 'bg-orange-500 shadow-md hover:scale-105 hover:shadow-lg'
          }
          ${isDragging ? 'scale-110 cursor-grabbing' : ''}
        `}
      >
        {marker.number}

        {/* Delete button - shown on hover or active */}
        <button
          onClick={handleDeleteClick}
          className={`
            absolute -top-1 -right-1 w-4 h-4 sm:w-5 sm:h-5
            bg-white dark:bg-gray-800 rounded-full
            flex items-center justify-center
            shadow-md border border-gray-200 dark:border-gray-600
            text-gray-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30
            transition-all duration-200
            ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
          `}
        >
          <XIcon className='w-2.5 h-2.5 sm:w-3 sm:h-3' />
        </button>
      </div>

      {/* Pulse animation for new markers */}
      {isActive && !isDragging && (
        <div className='absolute inset-0 -m-1'>
          <div className='absolute inset-0 rounded-full bg-orange-500 animate-ping opacity-20' />
        </div>
      )}
    </div>
  );
}

export default memo(MarkerPin);
